import React, { useState, useEffect, useCallback } from 'react';
import LoginForm from '@/components/LoginForm';
import Dashboard from '@/components/Dashboard';
import UploadImage from './UploadImage';
import { useQueryClient } from '@tanstack/react-query';
import { setAuthToken, clearAuthToken } from '@/lib/authToken';
import { isTokenExpired } from '@/lib/api';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';

const Index = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const queryClient = useQueryClient();
  const { fetchUserPrivileges, clearPrivileges } = useUserPrivileges();

  // Simplified token validation on app startup - no refresh, just check expiration
  useEffect(() => {
    const validateTokenOnStartup = async () => {
      try {
        const savedToken = localStorage.getItem('token');
        const savedUsername = localStorage.getItem('username');
        
        if (!savedToken || !savedUsername) {
          console.log('No saved token or username found');
          setIsValidatingToken(false);
          return;
        }

        console.log('Validating saved token on startup...');
        
        // Check if token is expired
        if (isTokenExpired()) {
          console.log('Token is expired, showing login form');
          // Clear all auth data
          clearAuthToken();
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('username');
          setToken(null);
          setUsername('');
          setIsLoggedIn(false);
        } else {
          console.log('Token is valid, proceeding with saved session');
          // Token is valid, proceed with saved session – fetch privileges on every refresh so UI (e.g. invoice report edit/delete) stays correct
          fetchUserPrivileges();
          const tokenWithBearer = `Bearer ${savedToken}`;
          setToken(tokenWithBearer);
          setUsername(savedUsername);
          setIsLoggedIn(true);
        }
      } catch (error) {
        console.error('Error validating token on startup:', error);
        // Clear auth data on error
        clearAuthToken();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('username');
        setToken(null);
        setUsername('');
        setIsLoggedIn(false);
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateTokenOnStartup();
  }, [fetchUserPrivileges]);

  // Fetch user privileges whenever user is logged in (after login or token validation)
  useEffect(() => {
    if (isLoggedIn) {
      fetchUserPrivileges();
    }
  }, [isLoggedIn, fetchUserPrivileges]);

  const handleLoginSuccess = (token: string, username: string) => {
    // Store token globally and in localStorage (without Bearer prefix)
    setAuthToken(token);
    localStorage.setItem('username', username);
    // Clear stored tab so Dashboard shows Transactions by default after login/signup
    sessionStorage.removeItem('currentPage');
    
    // Update state with Bearer prefix for display
    const tokenWithBearer = `Bearer ${token}`;
    setToken(tokenWithBearer);
    setUsername(username);
    setIsLoggedIn(true);
  };

  const handleLogout = useCallback(() => {
    // Clear React Query cache
    queryClient.clear();
    // Clear user privileges
    clearPrivileges();
    // Clear token globally and localStorage
    clearAuthToken();
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('username');
    // Update state
    setIsLoggedIn(false);
    setUsername('');
    setToken(null);
  }, [queryClient, clearPrivileges]);

  // Listen for mobile web exit confirm (when user taps OK on exit popup)
  useEffect(() => {
    const handleMobileWebLogout = () => {
      handleLogout();
    };
    window.addEventListener('mobile-web-logout', handleMobileWebLogout);
    return () => window.removeEventListener('mobile-web-logout', handleMobileWebLogout);
  }, [handleLogout]);

  // Show loading while validating token
  if (isValidatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating session...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {!isLoggedIn ? (
        <LoginForm onLoginSuccess={handleLoginSuccess} />
      ) : (
        <Dashboard 
          username={username} 
          onLogout={handleLogout} 
          token={token || ''} 
        />
      )}
    </>
  );
};

export default Index;
